import { useEffect, useState } from "react";
import { apiClient, ApiError } from "../../lib/api-client";
import { formatDateSaoPaulo } from "../../lib/currency";
import { usePagination } from "../../hooks/usePagination";
import { Pagination } from "../../components/Pagination";

interface AdminUser {
  id: string;
  displayName: string | null;
  email: string;
  status: string;
  createdAt: string;
}

export function AdminUsersPage() {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState({ email: "", displayName: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const pagination = usePagination(admins, 15);

  function loadAdmins() {
    setLoading(true);
    apiClient
      .get<{ items: AdminUser[] }>("/admin/admins")
      .then((res) => setAdmins(res.items))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Falha ao listar administradores."))
      .finally(() => setLoading(false));
  }

  useEffect(loadAdmins, []);

  async function handleCreateAdmin(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);
    setError(null);
    try {
      await apiClient.post("/admin/admins", form);
      setMessage(`Administrador ${form.email} autorizado com sucesso.`);
      setForm({ email: "", displayName: "", password: "" });
      loadAdmins();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao cadastrar administrador.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <h2>Administradores Datasci</h2>
      <p className="text-muted">
        Usuários com papel <code>datasci_admin</code> e acesso permitido via Cloudflare Access ou autenticação direta com senha.
      </p>

      {message && <p role="status" className="notice-success">{message}</p>}
      {error && <p role="alert" className="notice-error">{error}</p>}

      <details className="card" style={{ marginBottom: "var(--space-6)" }}>
        <summary style={{ cursor: "pointer", fontWeight: 600 }}>+ Autorizar novo administrador</summary>
        <form onSubmit={handleCreateAdmin} style={{ marginTop: "var(--space-4)", maxWidth: 460 }}>
          <div className="field">
            <label htmlFor="adminEmail">E-mail corporativo</label>
            <input
              id="adminEmail"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="nome@datasci.com.br"
            />
          </div>

          <div className="field">
            <label htmlFor="adminName">Nome de Exibição</label>
            <input
              id="adminName"
              type="text"
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="Ex.: Maria Silva"
            />
          </div>

          <div className="field">
            <label htmlFor="adminPassword">Senha de Acesso (Mínimo 6 caracteres)</label>
            <input
              id="adminPassword"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="••••••••"
              minLength={6}
            />
          </div>

          <button className="btn" type="submit" disabled={submitting} style={{ marginTop: "var(--space-3)" }}>
            {submitting ? "Cadastrando..." : "Autorizar Administrador"}
          </button>
        </form>
      </details>

      <div className="card" style={{ overflowX: "auto" }}>
        {loading ? (
          <p className="text-muted">Carregando administradores...</p>
        ) : admins.length === 0 ? (
          <p className="text-muted">Nenhum administrador cadastrado nesta base local.</p>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Status</th>
                  <th>Data de Criação</th>
                </tr>
              </thead>
              <tbody>
                {pagination.paginatedItems.map((adm) => (
                  <tr key={adm.id}>
                    <td><strong>{adm.displayName ?? "Administrador"}</strong></td>
                    <td><code>{adm.email}</code></td>
                    <td>
                      <span className="status-badge status-badge--paid">{adm.status}</span>
                    </td>
                    <td>{formatDateSaoPaulo(adm.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              currentPage={pagination.currentPage}
              totalItems={pagination.totalItems}
              pageSize={pagination.pageSize}
              onPageChange={pagination.setCurrentPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
